// Labels and small formatting for the weekly driver competition. No figure is
// computed here: every number and every gate verdict arrives from
// get_driver_competition_leaderboard_v1. This file only decides how a verdict
// reads, and a missing figure always reads as missing, never as zero.

export type GateVerdict = 'pass' | 'fail' | 'pending'
export type Standing = 'qualified' | 'in_contention' | 'pending' | 'out'

export type CompetitionRow = {
  driver_id: string
  driver_name: string | null
  earnings_rank: number | null
  prize_rank: number | null
  prize_zar: number | string | null
  shifts: number | null
  day_shifts: number | null
  total_earnings_zar: number | string | null
  earnings_per_shift_zar: number | string | null
  earnings_per_trip_zar: number | string | null
  online_hours_avg: number | string | null
  acceptance_avg: number | string | null
  rating: number | string | null
  l_per_100km: number | string | null
  fuel_standard_l_per_100km: number | string | null
  late_handovers: number | null
  late_submissions: number | null
  missing_forms: number | null
  day_shifts_without_wash: number | null
  harsh_braking_events: number | null
  speeding_trips: number | null
  max_speed_kmh: number | string | null
  failed_gates: string[] | null
  pending_gates: string[] | null
  gates: Record<string, GateVerdict> | null
  standing: Standing
  payroll_period_end: string | null
  board_linked?: boolean
}

export type GateSummary = { gate: string; fail: number; pending: number; pass: number; final: boolean }

export type Leaderboard = {
  week_start: string
  week_end: string
  week_open: boolean
  status: 'computed' | 'approved' | 'paid' | 'not_computed'
  computed_at: string | null
  notes: string | null
  prizes: (number | string | null)[]
  weeks: string[]
  gate_summary: GateSummary[]
  rows: CompetitionRow[]
}

// Written the way a driver would say it, because these are the words a Driver
// Servant will read out when a man asks why he is not on the list.
export const GATE_LABELS: Record<string, string> = {
  zero_lateness: 'Late for a handover',
  submission_on_time: 'Forms not all in within 2 hours of shift end',
  day_shift_carwash: 'Day shift without a carwash',
  earnings_floor: 'Average below R1,000 a shift',
  online_hours: 'Online 9 hours or less on average',
  acceptance_rate: 'Acceptance below 40%',
  earnings_per_trip: 'Average fare R65 or more',
  fuel_at_or_below_standard: 'Fuel above the standard for the car',
  no_harsh_braking: 'More than 3 harsh braking events',
  no_speeding: 'Speeding recorded',
  uber_rating: 'Uber rating below 4.70',
  no_traffic_fines: 'Traffic fine',
  no_final_written_warning: 'Final written warning in force',
  no_at_fault_accident: 'At-fault accident',
  no_conduct_incident: 'Safety or conduct incident',
  minimum_shifts: 'Fewer shifts than the weekly minimum',
}

// What a driver can do about each gate. Shown to the driver on his own board,
// beside the gate he is missing, so the screen answers "what now" and not only
// "why not". Gates that record something already done say so plainly rather
// than pretending there is a fix this week.
export const GATE_ADVICE: Record<string, string> = {
  zero_lateness: 'Be at your partner on time for every handover. A late handover cannot be undone this week; start clean on Monday.',
  submission_on_time: 'Send Part A, Part B and Part C within 2 hours of shift end: by 6pm on a day shift, by 6am on a night shift. A missing form counts as late.',
  day_shift_carwash: 'On a day shift, wash the car and answer Yes to the wash question on Part A.',
  earnings_floor: 'Your average per shift is below R1,000. Stay online, accept trips and work the busy hours to lift it before Sunday.',
  online_hours: 'Stay online more than 9 hours a shift on average. There is still time this week to lift the average.',
  acceptance_rate: 'Accept at least 40% of the trips you are offered. Short trips add up: the drivers earning most accept the most.',
  earnings_per_trip: 'That week had a rule that your average fare stay under R65. The rule was dropped from the week of 28 September 2026, because the fleet earns most at R50 to R75 a trip.',
  fuel_at_or_below_standard: 'Your fuel is above the standard for your car. Drive smoothly, avoid idling, and tell your Driver Servant if you think the car has a fault.',
  no_harsh_braking: 'The tracker recorded more than 3 harsh braking events this week. Up to 3 are allowed, because a hard stop can be you avoiding an accident. Keep your distance and brake early.',
  no_speeding: 'The tracker recorded speeding: 10 seconds or more at over 20 km/h above the posted limit where the limit is known from the road signs, or over 130 km/h anywhere. Until the week of 5 October 2026 the rule was any trip over 120 km/h. Keep to the limit.',
  uber_rating: 'Your Uber rating is below 4.70. A clean car, a greeting and a smooth drive lift it.',
  no_traffic_fines: 'A traffic fine was recorded against you for this week.',
  no_final_written_warning: 'A final written warning is in force. Speak to your Driver Servant about when it lapses.',
  no_at_fault_accident: 'An accident was recorded where you were found at fault.',
  no_conduct_incident: 'A safety or conduct incident was recorded. Speak to your Driver Servant.',
  minimum_shifts: 'The prize needs a working week. Speak to your Driver Servant if you were rostered fewer shifts.',
}

export function gateAdvice(key: string): string {
  return GATE_ADVICE[key] ?? 'Ask your Driver Servant what this means for you.'
}

export function gateLabel(key: string): string {
  return GATE_LABELS[key] ?? key.replace(/_/g, ' ')
}

export const STANDING_TEXT: Record<Standing, { label: string; tone: 'green' | 'amber' | 'neutral' | 'red' }> = {
  qualified: { label: 'Qualifies', tone: 'green' },
  in_contention: { label: 'Still in it', tone: 'amber' },
  pending: { label: 'Waiting on data', tone: 'neutral' },
  out: { label: 'Out this week', tone: 'red' },
}

function num(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

export function rand(v: number | string | null | undefined): string {
  const n = num(v)
  if (n === null) return '—'
  return 'R' + Math.round(n).toLocaleString('en-ZA').replace(/ /g, ' ')
}

export function fixed(v: number | string | null | undefined, digits = 1): string {
  const n = num(v)
  return n === null ? '—' : n.toFixed(digits)
}

export function percent(v: number | string | null | undefined): string {
  const n = num(v)
  return n === null ? '—' : `${Math.round(n * 100)}%`
}

// Dates arrive as YYYY-MM-DD. Read the parts directly so no browser time zone
// can move a Monday to a Sunday.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return '—'
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}`
}

export function weekLabel(start: string, end: string): string {
  return `${shortDate(start)} to ${shortDate(end)}`
}

// Coaching, the zone guide, approval support and rule proposals. Shapes of what
// driver_coaching_v1, driver_zone_guide_v1,
// driver_competition_approval_summary_v1 and get_competition_rule_proposals_v1
// return. Every figure is computed there.

export type CoachingItem = { key: string; title?: string; detail: string; gain_per_shift_zar?: number | string | null }

export type Coaching =
  | { state: 'no_shifts'; from: string; to: string }
  | {
      state: 'ok'
      from: string
      to: string
      shifts: number
      shifts_with_uber_hours: number
      confidence: 'normal' | 'low'
      refreshed_at: string | null
      measures: {
        earnings_per_shift: number | null
        peer_index: number | null
        fleet_median_on_your_dates: number | null
        online_hours: number | null
        acceptance: number | null
        rand_per_online_hour: number | null
        fleet_rand_per_online_hour_on_your_dates: number | null
        average_fare: number | null
        busy_share: number | null
        trough_share: number | null
        main_zone: string | null
      }
      levers: CoachingItem[]
      keep_going: boolean
      not_on_you: CoachingItem[]
      staff_notes: CoachingItem[]
    }

export type ZoneCell = { zone: string; rph: number | null; eps: number | null; shifts: number; zar_per_km: number | null; busy_share: number | null }
export type ZonePhase = { phase: string; window: string; fleet_rph: number | null; fleet_eps: number | null; shifts: number; zones: ZoneCell[] }
export type ZoneGuideData = {
  as_of: string
  days: number
  refreshed_at: string | null
  basis: string
  phases: ZonePhase[]
  weekdays: { dow: number; window: string; rph: number | null; eps: number | null; shifts: number }[]
}

export const PHASE_LABELS: Record<string, string> = {
  days_25_to_5: '25th to 5th, around payday',
  days_6_to_14: '6th to 14th',
  days_15_to_24: '15th to 24th, the quiet part of the month',
}

export const WINDOW_LABELS: Record<string, string> = { am_pm: 'Day shift', pm_am: 'Night shift' }

export const WEEKDAY_LABELS = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

export type ApprovalSummary =
  | { state: 'not_computed'; week_start: string }
  | {
      state: 'ok'
      week_start: string
      week_end: string
      status: string
      computed_at: string | null
      payroll_period_end: string | null
      drivers: number
      qualified: number
      prize_total_zar: number | string
      winners: { prize_rank: number; driver_name: string; total_earnings_zar: number | string; shifts: number; prize_zar: number | string; why: string }[]
      one_gate_away: { driver_name: string; total_earnings_zar: number | string; shifts: number; gate: string; kind: 'failed' | 'pending' }[]
      gate_summary: { gate: string; fail: number; pending: number }[]
      checks: { check: string; ok: boolean; detail: string }[]
      ready_to_approve: boolean
      summary: string
    }

export type SimWinner = { rank: number; name: string; total: number; shifts: number }
export type SimWeek = {
  week_start: string
  drivers: number
  before: { qualified: number; winners: SimWinner[] }
  after: { qualified: number; winners: SimWinner[]; one_gate_away: { name: string; total: number; gate: string }[] }
  gate_fail_before: Record<string, number>
  gate_fail_after: Record<string, number>
}
export type RuleProposal = {
  id: string
  title: string
  overrides: Record<string, string>
  rationale: { change: string; setting: string; evidence: string; effect?: string }[]
  weeks: string[]
  simulation: { base_rules: Record<string, string>; weeks: SimWeek[] } | null
  simulated_at: string | null
  proposed_by: string
  created_at: string
  decision: string | null
  decided_by: string | null
  decided_at: string | null
  decision_note: string | null
}

export const CHECK_LABELS: Record<string, string> = {
  week_closed: 'The week has ended',
  computed_after_close: 'Computed after the week ended',
  source_unchanged: 'Nothing changed since the compute',
  uber_hours_present: 'Uber hours are in for every shift',
  no_pending_near_the_top: 'Nobody near the top is waiting on data',
}
